<script lang="ts" setup>
import { computed, PropType } from 'vue';
import {
  TirPmPopover,
  PopoverPositionEnum,
  PopoverColorEnum,
  TirPmIconView,
  TirPmScrollbar,
  TirPmTooltipElement,
} from 'tir-components';
import { TaskParameterModel } from '@/components/task-parameter';
import { TaskPersonModel } from '@/components/tasks-kanban/models';
import { TaskParameter, TaskParameterTypeEnum } from '@/components';

const props = defineProps({
  /**Статус */
  status: { type: Object as PropType<TaskParameterModel> },
  /**Приоритет */
  priority: { type: Object as PropType<TaskParameterModel> },
  /**Тип */
  type: { type: Object as PropType<TaskParameterModel> },
  /**Список исполнителей */
  performers: { type: Array as PropType<Array<TaskPersonModel>> },
  /**Автор */
  author: { type: Object as PropType<TaskPersonModel> },
  /**Список тегов */
  tags: { type: Array as PropType<Array<TaskParameterModel>> },
});

/**Информация о параметре */
const parameterInfo = computed(() => {
  const params = [
    { Data: props.status, Type: TaskParameterTypeEnum.Status },
    { Data: props.priority, Type: TaskParameterTypeEnum.Priority },
    { Data: props.type, Type: TaskParameterTypeEnum.Type },
    { Data: props.tags, Type: TaskParameterTypeEnum.Tag },
  ];

  return params.find((param) => param.Data);
});

/**Показанный исполнитель */
const shownPerformer = computed<TaskPersonModel | undefined>(() => {
  if (!props.performers?.length) return;
  return props.performers[0];
});
/**Скрытые исполнители */
const hidePerformers = computed(() => props.performers?.slice(1));
/**Скрытые теги */
const hideTags = computed(() => props.tags?.slice(1));

/**Кол-во скрытых элементов */
const hideCount = computed(() => {
  let count = 0;
  if (props.tags) count = props.tags.length - 1;
  else if (props.performers) count = props.performers.length - 1;

  return Math.max(count, 0);
});
</script>

<template>
  <div class="task-card-groupping-param">
    <template v-if="parameterInfo">
      <template v-if="Array.isArray(parameterInfo.Data)">
        <TaskParameter
          v-if="parameterInfo.Data.length"
          preview
          :type="parameterInfo.Type"
          :data="parameterInfo.Data[0]"
          hideRemove
        />
      </template>
      <TaskParameter
        v-else
        preview
        :type="parameterInfo.Type"
        :data="parameterInfo.Data"
        hideRemove
      />
    </template>
    <TirPmTooltipElement v-else-if="author">
      <TirPmIconView
        :size="24"
        isRounded
        :colorCode="author.NoPhotoColor"
        :isBordered="false"
        :imageLoadUrl="author.ImageUrl"
      >
        <template #mainSlot>
          {{ author.FirstName[0] + author.LastName[0] }}
        </template>
      </TirPmIconView>

      <template #content>
        <div class="task__popover-title">
          {{ author.FirstName }} {{ author.MiddleName }} {{ author.LastName }}
        </div>
      </template>
    </TirPmTooltipElement>
    <template v-else-if="shownPerformer">
      <TirPmTooltipElement :key="shownPerformer.Id">
        <template #default>
          <TirPmIconView
            is-rounded
            :size="24"
            :imageLoadUrl="shownPerformer.ImageUrl"
            :colorCode="shownPerformer.NoPhotoColor"
            :is-bordered="false"
          >
            <template #mainSlot>
              {{ shownPerformer.FirstName[0] + shownPerformer.LastName[0] }}
            </template>
          </TirPmIconView>
        </template>
        <template #content>
          <div class="task-card_user-data">
            {{ shownPerformer.LastName }} {{ shownPerformer.FirstName }}
            {{ shownPerformer.MiddleName }}
          </div>
        </template>
      </TirPmTooltipElement>
    </template>
    <TirPmPopover
      v-if="hideCount > 0"
      :isHideTriangle="false"
      is-tooltip
      :color="PopoverColorEnum.Black"
      @click.stop
    >
      <template #default>
        <div v-text="`+${hideCount}`" class="task-card_circle" />
      </template>
      <template #content>
        <TirPmScrollbar class="task-card_scrollbar" :show-on-hover="true">
          <div class="task-card_scrollbar-content">
            <template v-if="hidePerformers?.length">
              <div
                v-for="person in hidePerformers"
                :key="person.Id"
                class="task-card_hidden-users-content"
              >
                <TirPmIconView
                  is-rounded
                  :size="24"
                  :imageLoadUrl="person.ImageUrl"
                  :colorCode="person.NoPhotoColor"
                  :is-bordered="false"
                >
                  <template #mainSlot>
                    {{ person.FirstName[0] + person.LastName[0] }}
                  </template>
                </TirPmIconView>
                {{ person.LastName }} {{ person.FirstName }} {{ person.MiddleName }}
              </div>
            </template>
            <template v-else-if="hideTags?.length">
              <div v-for="tag in hideTags" :key="tag.Id" class="task-card_hidden-tags-content">
                <TaskParameter preview :type="TaskParameterTypeEnum.Tag" :data="tag" hideRemove />
              </div>
            </template>
          </div>
        </TirPmScrollbar>
      </template>
    </TirPmPopover>
  </div>
</template>

<style lang="scss" scoped>
.task-card-groupping-param {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  flex-shrink: 0;
  padding-right: 0.75rem;
}
.task__popover-title {
  display: flex;
  justify-content: center;
  overflow-wrap: break-word;
  padding: 1rem;
  font-size: 0.75rem;
}
.task-card_user-data {
  display: flex;
  justify-content: center;
  overflow-wrap: break-word;
  padding: 1rem;
}
.task-card_hidden-users-content,
.task-card_hidden-tags-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 1rem;
}
.task-card_circle {
  cursor: pointer;
}
.task-card_scrollbar {
  max-height: 15rem;
}
.task-card_scrollbar-content {
  padding: 0.5rem 0;
}
</style>
